import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { CATEGORIES } from "../lib/categories";
import { Plus, Edit, Trash2, X, Package, Users, ShoppingBag, DollarSign, Mail, Upload, Image, Loader2, Eye, EyeOff, Copy } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", description: "", price: 0, category: "", stock: 10, materials: "", images: [""], featured: false };

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [emails, setEmails] = useState([]);
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const [imagesText, setImagesText] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/login");
  }, [user, loading, navigate]);

  const reload = async () => {
    const [p, o, u, s, e] = await Promise.all([
      api.get("/admin/products"),
      api.get("/admin/orders"),
      api.get("/admin/users"),
      api.get("/admin/stats"),
      api.get("/admin/emails"),
    ]);
    setProducts(p.data); setOrders(o.data); setUsers(u.data); setStats(s.data); setEmails(e.data);
  };

  useEffect(() => { if (user?.role === "admin") reload(); }, [user]);

  const save = async (e) => {
    e.preventDefault();
    const imgList = imagesText.split("\n").map((s) => s.trim()).filter(Boolean);
    const data = { ...form, category: form.category || null, price: parseFloat(form.price), stock: parseInt(form.stock), images: imgList };
    try {
      if (editing) await api.put(`/products/${editing}`, data);
      else await api.post("/products", data);
      toast.success("Produit enregistré");
      setShowForm(false); setEditing(null); setForm(empty); setImagesText("");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer définitivement ce produit ?\n\nAstuce : vous pouvez aussi le masquer pour le garder en base.")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Produit supprimé");
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const toggleVisibility = async (id, currentlyHidden) => {
    try {
      await api.put(`/products/${id}/visibility`);
      setProducts((prev) => prev.map((p) => p.id === id ? { ...p, hidden: !currentlyHidden } : p));
      toast.success(currentlyHidden ? "Article visible dans la boutique" : "Article masqué de la boutique");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({ ...p, images: p.images.length ? p.images : [""] });
    setImagesText((p.images || []).join("\n"));
    setShowForm(true);
  };

  const duplicate = (p) => {
    setEditing(null);
    setForm({ name: p.name + " (copie)", description: p.description, price: p.price, category: p.category || "", stock: p.stock, materials: p.materials, images: p.images?.length ? p.images : [""], featured: false, hidden: true });
    setImagesText((p.images || []).join("\n"));
    setShowForm(true);
    toast.info("Copie prête — modifiez et enregistrez");
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    const newUrls = [];
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      try {
        const resp = await api.post("/admin/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
        const fullUrl = process.env.REACT_APP_BACKEND_URL + resp.data.url;
        newUrls.push(fullUrl);
      } catch (err) {
        toast.error(`Erreur pour ${file.name} : ${err.response?.data?.detail || "Échec"}`);
      }
    }
    if (newUrls.length) {
      const current = imagesText.trim();
      const updated = current ? current + "\n" + newUrls.join("\n") : newUrls.join("\n");
      setImagesText(updated);
      toast.success(`${newUrls.length} image(s) importée(s)`);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  if (!user || user.role !== "admin") return null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="admin-page">
      <h1 className="font-serif-display text-4xl mb-2">Tableau de bord</h1>
      <p className="text-sm text-[#6B655C] mb-10">Gestion de LabouniteShop</p>

      {/* STATS */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
          {[
            { icon: Package, label: "Produits", val: stats.products },
            { icon: ShoppingBag, label: "Commandes", val: stats.orders },
            { icon: Users, label: "Clients", val: stats.users },
            { icon: DollarSign, label: "Revenus", val: `${stats.revenue.toFixed(2)} €` },
            { icon: Mail, label: "Emails", val: stats.emails ?? 0 },
          ].map((s, i) => (
            <div key={i} className="bg-white border border-[#E5E1D8] rounded-2xl p-5">
              <s.icon className="text-terracotta mb-2" size={22} strokeWidth={1.5} />
              <p className="text-xs uppercase tracking-wider text-[#6B655C]">{s.label}</p>
              <p className="text-2xl font-medium mt-1">{s.val}</p>
            </div>
          ))}
        </div>
      )}

      {/* TABS */}
      <div className="flex gap-3 mb-6 border-b border-[#E5E1D8] flex-wrap">
        {["products", "orders", "users", "emails"].map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-sm uppercase tracking-wider ${tab === t ? "text-terracotta border-b-2 border-terracotta" : "text-[#6B655C]"}`} data-testid={`tab-${t}`}>
            {t === "products" ? "Produits" : t === "orders" ? "Commandes" : t === "users" ? "Clients" : "Emails"}
          </button>
        ))}
      </div>

      {/* PRODUCTS */}
      {tab === "products" && (
        <div>
          <button onClick={() => { setShowForm(true); setEditing(null); setForm(empty); }} className="btn-primary mb-6" data-testid="add-product-btn">
            <Plus size={16} /> Nouveau produit
          </button>
          <div className="bg-white border border-[#E5E1D8] rounded-2xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#F7F5F0] text-xs uppercase tracking-wider text-[#6B655C]">
                <tr><th className="text-left p-4">Produit</th><th className="text-left p-4">Catégorie</th><th className="text-left p-4">Prix</th><th className="text-left p-4">Stock</th><th className="text-left p-4">Visibilité</th><th className="p-4"></th></tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className={`border-t border-[#E5E1D8] ${p.hidden ? "opacity-50" : ""}`} data-testid={`admin-product-${p.id}`}>
                    <td className="p-4 font-medium">
                      {p.name}
                      {p.hidden && <span className="ml-2 text-[10px] uppercase tracking-wider bg-[#E5E1D8] text-[#6B655C] px-2 py-0.5 rounded-full">masqué</span>}
                    </td>
                    <td className="p-4 text-sm text-[#6B655C]">{p.category || "—"}</td>
                    <td className="p-4 text-terracotta">{p.price.toFixed(2)} €</td>
                    <td className="p-4 text-sm">{p.stock}</td>
                    <td className="p-4">
                      <button
                        onClick={() => toggleVisibility(p.id, p.hidden)}
                        className={`p-2 rounded-full transition ${p.hidden ? "text-[#6B655C] hover:text-terracotta" : "text-emerald-600 hover:text-emerald-700"}`}
                        title={p.hidden ? "Rendre visible" : "Masquer de la boutique"}
                        data-testid={`visibility-${p.id}`}
                      >
                        {p.hidden ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </td>
                    <td className="p-4 text-right space-x-1">
                      <button onClick={() => duplicate(p)} className="p-2 hover:text-blue-600" title="Dupliquer" data-testid={`dup-${p.id}`}><Copy size={16} /></button>
                      <button onClick={() => startEdit(p)} className="p-2 hover:text-terracotta" title="Modifier" data-testid={`edit-${p.id}`}><Edit size={16} /></button>
                      <button onClick={() => del(p.id)} className="p-2 hover:text-red-600" title="Supprimer" data-testid={`del-${p.id}`}><Trash2 size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ORDERS */}
      {tab === "orders" && (
        <div className="space-y-3" data-testid="admin-orders-tab">
          {orders.map((o) => {
            const displayStatus = o.shipping_status || o.payment_status || o.status;
            const statusMap = { pending: "En attente", initiated: "Initiée", completed: "Confirmée", paid: "Payée", preparing: "En préparation", shipped: "Expédiée", delivered: "Livrée" };
            const statusColors = { paid: "bg-patchwork/20 text-patchwork", completed: "bg-patchwork/20 text-patchwork", preparing: "bg-amber-100 text-amber-700", shipped: "bg-blue-100 text-blue-700", delivered: "bg-green-100 text-green-700" };
            return (
              <details key={o.id} className="bg-white border border-[#E5E1D8] rounded-2xl" data-testid={`admin-order-${o.id}`}>
                <summary className="p-4 cursor-pointer flex flex-wrap items-center gap-4 justify-between">
                  <div>
                    <p className="font-medium text-sm">{new Date(o.created_at).toLocaleDateString("fr-FR")} — {o.user_email}</p>
                    <p className="text-xs text-[#6B655C]">{o.items?.length || 0} article(s) — <span className="text-terracotta font-medium">{o.amount?.toFixed(2)} €</span></p>
                  </div>
                  <span className={`text-xs px-3 py-1 rounded-full font-medium ${statusColors[displayStatus] || "bg-[#E5E1D8] text-[#6B655C]"}`}>{statusMap[displayStatus] || displayStatus}</span>
                </summary>
                <div className="px-4 pb-4 border-t border-[#E5E1D8] pt-3">
                  <div className="space-y-1 mb-4">
                    {(o.items || []).map((it, i) => <p key={i} className="text-sm text-[#6B655C]">{it.quantity}× {it.name} — {(it.price * it.quantity).toFixed(2)} €</p>)}
                    {o.shipping && <p className="text-xs text-[#6B655C] mt-2">Livraison : {o.shipping.name} — {o.shipping.address}, {o.shipping.postal} {o.shipping.city}</p>}
                  </div>
                  <div className="flex flex-wrap gap-3 items-end bg-[#F7F5F0] rounded-xl p-3">
                    <div>
                      <label className="block text-xs text-[#6B655C] mb-1">Statut expédition</label>
                      <select
                        value={o.shipping_status || "pending"}
                        onChange={async (e) => {
                          const newStatus = e.target.value;
                          const trackNum = o.tracking_number || "";
                          try {
                            await api.put(`/admin/orders/${o.id}/tracking`, { shipping_status: newStatus, tracking_number: trackNum });
                            setOrders((prev) => prev.map((x) => x.id === o.id ? { ...x, shipping_status: newStatus } : x));
                            toast.success("Statut mis à jour");
                          } catch { toast.error("Erreur"); }
                        }}
                        className="p-2 rounded-xl border border-[#E5E1D8] text-sm bg-white"
                        data-testid="order-status-select"
                      >
                        <option value="pending">En attente</option>
                        <option value="preparing">En préparation</option>
                        <option value="shipped">Expédiée</option>
                        <option value="delivered">Livrée</option>
                      </select>
                    </div>
                    <div className="flex-1 min-w-[200px]">
                      <label className="block text-xs text-[#6B655C] mb-1">N° suivi Colissimo</label>
                      <div className="flex gap-2">
                        <input
                          defaultValue={o.tracking_number || ""}
                          placeholder="Ex: 6A12345678901"
                          className="flex-1 p-2 rounded-xl border border-[#E5E1D8] text-sm font-mono bg-white"
                          data-testid="order-tracking-input"
                          onKeyDown={async (e) => {
                            if (e.key === "Enter") {
                              const val = e.target.value.trim();
                              try {
                                await api.put(`/admin/orders/${o.id}/tracking`, { shipping_status: o.shipping_status || "shipped", tracking_number: val });
                                setOrders((prev) => prev.map((x) => x.id === o.id ? { ...x, tracking_number: val, shipping_status: o.shipping_status || "shipped" } : x));
                                toast.success("Numéro de suivi enregistré");
                              } catch { toast.error("Erreur"); }
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="px-3 py-2 bg-terracotta text-white rounded-xl text-sm hover:bg-terracotta/90 transition"
                          data-testid="order-tracking-save-btn"
                          onClick={async (e) => {
                            const input = e.target.closest(".flex").querySelector("input");
                            const val = input.value.trim();
                            try {
                              await api.put(`/admin/orders/${o.id}/tracking`, { shipping_status: o.shipping_status || "shipped", tracking_number: val });
                              setOrders((prev) => prev.map((x) => x.id === o.id ? { ...x, tracking_number: val, shipping_status: o.shipping_status || "shipped" } : x));
                              toast.success("Numéro de suivi enregistré");
                            } catch { toast.error("Erreur"); }
                          }}
                        >
                          OK
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </details>
            );
          })}
          {orders.length === 0 && <p className="text-[#6B655C]">Aucune commande pour le moment.</p>}
        </div>
      )}

      {/* USERS */}
      {tab === "users" && (
        <div className="bg-white border border-[#E5E1D8] rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#F7F5F0] text-xs uppercase tracking-wider text-[#6B655C]">
              <tr><th className="text-left p-4">Nom</th><th className="text-left p-4">Email</th><th className="text-left p-4">Rôle</th><th className="text-left p-4">Inscrit le</th></tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-[#E5E1D8]">
                  <td className="p-4">{u.name}</td>
                  <td className="p-4 text-sm">{u.email}</td>
                  <td className="p-4 text-sm">{u.role}</td>
                  <td className="p-4 text-sm">{new Date(u.created_at).toLocaleDateString("fr-FR")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* EMAILS */}
      {tab === "emails" && (
        <div data-testid="emails-tab">
          <p className="text-xs text-[#6B655C] mb-4">📬 Emails mockés (stockés dans la collection <code>email_logs</code>) — pour passer en production, branchez Resend / SendGrid.</p>
          {emails.length === 0 ? (
            <p className="text-[#6B655C] text-sm">Aucun email envoyé pour le moment.</p>
          ) : (
            <div className="space-y-3">
              {emails.map((e) => (
                <details key={e.id} className="bg-white border border-[#E5E1D8] rounded-2xl p-4" data-testid={`email-${e.id}`}>
                  <summary className="cursor-pointer flex flex-wrap items-center gap-4 justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-mustard">{e.type}</p>
                      <p className="font-medium">{e.subject}</p>
                    </div>
                    <div className="text-right text-xs text-[#6B655C]">
                      <p>→ {e.to}</p>
                      <p>{new Date(e.sent_at).toLocaleString("fr-FR")}</p>
                    </div>
                  </summary>
                  <pre className="mt-4 p-4 bg-[#F7F5F0] rounded-2xl text-xs whitespace-pre-wrap font-body text-[#1F1C18]/80">{e.body}</pre>
                </details>
              ))}
            </div>
          )}
        </div>
      )}

      {/* FORM MODAL */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-6 border-b border-[#E5E1D8]">
              <h2 className="font-serif-display text-2xl">{editing ? "Modifier" : "Nouveau"} produit</h2>
              <button onClick={() => setShowForm(false)}><X size={20} /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              <input required placeholder="Nom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full p-3 rounded-2xl border border-[#E5E1D8]" data-testid="form-name" />
              <textarea required placeholder="Description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full p-3 rounded-2xl border border-[#E5E1D8]" data-testid="form-description" />
              <div className="grid grid-cols-2 gap-3">
                <input required type="number" step="0.01" placeholder="Prix" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="p-3 rounded-2xl border border-[#E5E1D8]" data-testid="form-price" />
                <input required type="number" placeholder="Stock" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="p-3 rounded-2xl border border-[#E5E1D8]" data-testid="form-stock" />
              </div>
              <select value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full p-3 rounded-2xl border border-[#E5E1D8]" data-testid="form-category">
                <option value="">Sans catégorie</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input placeholder="Matières" value={form.materials} onChange={(e) => setForm({ ...form, materials: e.target.value })} className="w-full p-3 rounded-2xl border border-[#E5E1D8]" data-testid="form-materials" />
              {/* Image Upload */}
              <div className="space-y-3">
                <div
                  className="border-2 border-dashed border-[#E5E1D8] rounded-2xl p-6 text-center hover:border-terracotta transition cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="upload-zone"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    multiple
                    className="hidden"
                    onChange={handleFileUpload}
                    data-testid="file-input"
                  />
                  {uploading ? (
                    <div className="flex items-center justify-center gap-2 text-terracotta">
                      <Loader2 size={20} className="animate-spin" />
                      <span className="text-sm">Import en cours...</span>
                    </div>
                  ) : (
                    <>
                      <Upload size={28} className="mx-auto text-[#6B655C] mb-2" />
                      <p className="text-sm text-[#6B655C]">Cliquez pour importer des photos</p>
                      <p className="text-xs text-[#6B655C]/70 mt-1">JPG, PNG, WebP — max 10 Mo par image</p>
                    </>
                  )}
                </div>

                {/* Image previews */}
                {imagesText.trim() && (
                  <div className="flex flex-wrap gap-2">
                    {imagesText.split("\n").filter(Boolean).map((url, i) => (
                      <div key={i} className="relative group">
                        <img src={url.trim()} alt={`Photo ${i + 1}`} className="w-16 h-16 rounded-xl object-cover border border-[#E5E1D8]" onError={(e) => { e.target.style.display = "none"; }} />
                        <button
                          type="button"
                          onClick={() => {
                            const lines = imagesText.split("\n").filter(Boolean);
                            lines.splice(i, 1);
                            setImagesText(lines.join("\n"));
                          }}
                          className="absolute -top-1 -right-1 bg-terracotta text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <details className="text-xs text-[#6B655C]">
                  <summary className="cursor-pointer hover:text-terracotta">Ou coller des URLs d'images</summary>
                  <textarea
                    placeholder="URLs des images (une par ligne)"
                    rows={3}
                    value={imagesText}
                    onChange={(e) => setImagesText(e.target.value)}
                    className="w-full mt-2 p-3 rounded-2xl border border-[#E5E1D8] text-xs font-mono"
                    data-testid="form-images"
                  />
                </details>
                <p className="text-xs text-[#6B655C]">2-4 images pour activer la galerie produit.</p>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="form-featured" />
                Coup de cœur (mise en avant)
              </label>
              <button type="submit" className="btn-primary w-full justify-center" data-testid="form-submit">Enregistrer</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
